// =====================================================================
// Progresso de meta — matemática pura e testável do alvo × realizado.
// Resolve a OPEN_QUESTION A5 (o "alvo" da meta era configurável depois).
// Calcula, para uma competência (mês) × região:
//   • % atendida        = realizado / meta
//   • % do ritmo        = quão adiantado/atrasado está vs. a fração do mês já
//                         decorrida (linear). 100% = exatamente no ritmo.
//   • necessidade de    = veículos ainda necessários para fechar a meta no
//     veículos            tempo restante (premissa: cargas por veículo/dia).
//
// `today` é injetável para testes determinísticos; em produção usa a data atual.
// Premissa (documentada): ritmo é LINEAR por dia de calendário do mês. Meses já
// encerrados têm fração decorrida = 1 (ritmo passa a ser igual à % atendida).
// =====================================================================

export type RitmoStatus = "sem_meta" | "nao_iniciado" | "adiantado" | "no_prazo" | "atrasado";

export interface MetaProgressInput {
  metaCargas: number | null; // alvo editável (cargas). null = sem meta definida
  realizado: number; // cargas CARREGADAS na competência
  competencia: string; // 'YYYY-MM-DD' (1º dia do mês de referência)
  today?: Date; // injeção p/ testes; default: agora
  cargasPorVeiculoDia?: number; // premissa; default 1
}

export interface MetaProgress {
  metaCargas: number | null;
  realizado: number;
  cargasRestantes: number | null;
  pctAtendida: number | null; // 0..1+ (ex.: 0.83 = 83%)
  fracaoDecorrida: number; // 0..1
  pctRitmo: number | null; // 0..1+ (ex.: 1.10 = 10% adiantado)
  ritmoStatus: RitmoStatus;
  veiculosNecessarios: number | null;
  diasNoMes: number;
  diasDecorridos: number;
  diasRestantes: number;
}

function daysInMonthUTC(year: number, month0: number): number {
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}

export function computeMetaProgress(input: MetaProgressInput): MetaProgress {
  const { metaCargas, realizado } = input;
  const today = input.today ?? new Date();
  const cpvd = input.cargasPorVeiculoDia && input.cargasPorVeiculoDia > 0 ? input.cargasPorVeiculoDia : 1;

  const comp = new Date(input.competencia);
  const year = comp.getUTCFullYear();
  const month0 = comp.getUTCMonth();
  const diasNoMes = daysInMonthUTC(year, month0);

  // Posição de "hoje" relativa ao mês da competência.
  const compStart = Date.UTC(year, month0, 1);
  const nextMonth = Date.UTC(year, month0 + 1, 1);
  const nowUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

  let diasDecorridos: number;
  if (nowUtc >= nextMonth) diasDecorridos = diasNoMes; // mês encerrado
  else if (nowUtc < compStart) diasDecorridos = 0; // mês ainda não começou
  else diasDecorridos = today.getUTCDate(); // mês corrente

  const diasRestantes = Math.max(diasNoMes - diasDecorridos, 0);
  const fracaoDecorrida = diasDecorridos / diasNoMes;

  // Sem meta: reporta só o realizado.
  if (metaCargas == null || !(metaCargas > 0)) {
    return {
      metaCargas: metaCargas ?? null,
      realizado,
      cargasRestantes: null,
      pctAtendida: null,
      fracaoDecorrida,
      pctRitmo: null,
      ritmoStatus: "sem_meta",
      veiculosNecessarios: null,
      diasNoMes,
      diasDecorridos,
      diasRestantes,
    };
  }

  const cargasRestantes = Math.max(metaCargas - realizado, 0);
  const pctAtendida = realizado / metaCargas;

  // Ritmo: realizado vs. o esperado linear (meta × fração decorrida).
  let pctRitmo: number | null = null;
  let ritmoStatus: RitmoStatus;
  if (diasDecorridos <= 0) {
    ritmoStatus = "nao_iniciado";
  } else {
    const esperado = metaCargas * fracaoDecorrida;
    pctRitmo = esperado > 0 ? realizado / esperado : null;
    if (pctRitmo == null) ritmoStatus = "no_prazo";
    else if (pctRitmo >= 1.05) ritmoStatus = "adiantado";
    else if (pctRitmo >= 0.95) ritmoStatus = "no_prazo";
    else ritmoStatus = "atrasado";
  }

  // Necessidade de veículos: para fechar o que falta no tempo restante.
  // Mês corrente → veículos/dia p/ terminar no prazo; mês encerrado (0 dias
  // restantes) → o que falta é backlog, todo "necessário agora".
  const denom = Math.max(diasRestantes, 1) * cpvd;
  const veiculosNecessarios = cargasRestantes === 0 ? 0 : Math.ceil(cargasRestantes / denom);

  return {
    metaCargas,
    realizado,
    cargasRestantes,
    pctAtendida,
    fracaoDecorrida,
    pctRitmo,
    ritmoStatus,
    veiculosNecessarios,
    diasNoMes,
    diasDecorridos,
    diasRestantes,
  };
}

// Rótulos pt-BR do status de ritmo (para a UI).
export const RITMO_LABEL: Record<RitmoStatus, string> = {
  sem_meta: "Sem meta",
  nao_iniciado: "Não iniciado",
  adiantado: "Adiantado",
  no_prazo: "No prazo",
  atrasado: "Atrasado",
};
